/* Notificaciones nativas del navegador (Web Notifications API).
 *
 * Envoltura minima con guardas de soporte para avisar al usuario cuando llega
 * una nueva aprobacion HITL a la bandeja mientras la pestana esta en segundo
 * plano. Todo es best-effort: si el navegador no soporta Notification, el
 * permiso esta denegado, o la construccion falla, no rompemos nada.
 */

/** true si el navegador expone la Web Notifications API. */
export function notificationsSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

/** Estado actual del permiso ('default' | 'granted' | 'denied'), o 'denied' si no hay soporte. */
export function notificationPermission(): NotificationPermission {
  return notificationsSupported() ? Notification.permission : "denied";
}

/**
 * Pide permiso al usuario para mostrar notificaciones. DEBE llamarse tras un
 * gesto del usuario (click) — los navegadores penalizan o auto-deniegan los
 * prompts sin interaccion. Idempotente: si ya esta granted/denied, no vuelve
 * a preguntar. Nunca lanza.
 */
export async function ensureNotificationPermission(): Promise<NotificationPermission> {
  if (!notificationsSupported()) return "denied";
  if (Notification.permission !== "default") return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    // Safari antiguo usa la firma con callback; si la promesa falla, no bloqueamos.
    return Notification.permission;
  }
}

export interface ShowNotificationOptions {
  body?: string;
  /** Agrupa/colapsa notificaciones con el mismo tag (evita apilar duplicados). */
  tag?: string;
  /** Handler de click: normalmente enfoca la ventana y navega al detalle. */
  onClick?: () => void;
}

/**
 * Muestra una notificacion si hay permiso. Devuelve la instancia o null.
 * Best-effort: cualquier fallo (permiso, construccion) se traga y devuelve null.
 */
export function showNotification(
  title: string,
  { body, tag, onClick }: ShowNotificationOptions = {},
): Notification | null {
  if (notificationPermission() !== "granted") return null;
  try {
    const notification = new Notification(title, { body, tag });
    if (onClick) {
      notification.onclick = () => {
        window.focus();
        onClick();
        notification.close();
      };
    }
    return notification;
  } catch {
    return null;
  }
}
